import React, {
  FC,
  useRef,
  useState,
  ReactNode,
  useEffect,
  ForwardedRef,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react'
import cn from 'classnames'

import { keys } from 'common/utils/forms/keys'

export const sizeClassNames = {
  default: '',
  large: 'form-control-lg',
  small: 'form-control-sm',
} as const

export type InputSize = keyof typeof sizeClassNames

type InputElement = HTMLInputElement | HTMLTextAreaElement

export type InputType = Omit<
  React.InputHTMLAttributes<InputElement>,
  'size'
> & {
  inputClassName?: string
  inputRef?: ForwardedRef<InputElement>
  isValid?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  onIconLeftClick?: () => void
  onIconRightClick?: () => void
  shouldValidate?: boolean
  size?: InputSize
  textarea?: boolean
  touched?: boolean
}

const Input: FC<InputType> = ({
  children,
  className,
  disabled,
  iconLeft,
  iconRight,
  id,
  inputClassName,
  inputRef,
  isValid = true,
  name,
  onBlur,
  onFocus,
  onIconLeftClick,
  onIconRightClick,
  onKeyDown,
  placeholder = ' ',
  shouldValidate: _shouldValidate,
  size = 'default',
  textarea,
  touched,
  type,
  value,
  ...rest
}) => {
  const [shouldValidate, setShouldValidate] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const internalRef = useRef<InputElement>(null)

  const ref = inputRef ?? internalRef

  useEffect(() => {
    if (_shouldValidate) {
      setShouldValidate(true)
    }
  }, [_shouldValidate])

  // Event handlers
  const focusHandler: FocusEventHandler<InputElement> = (e) => {
    setIsFocused(true)
    onFocus?.(e)
  }

  const blurHandler: FocusEventHandler<InputElement> = (e) => {
    setShouldValidate(true)
    setIsFocused(false)
    onBlur?.(e)
  }

  const keyDownHandler: KeyboardEventHandler<InputElement> = (e) => {
    if (keys.isEscape(e)) {
      const currentRef = typeof ref === 'function' ? null : ref?.current
      currentRef?.blur()
    }
    onKeyDown?.(e)
  }

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev)
  }

  // Computed class names
  const containerClassName = cn(
    'position-relative input-container',
    {
      focused: isFocused,
      'input-container--with-left-icon': !!iconLeft,
      'input-container--with-right-icon': !!iconRight,
      invalid: (shouldValidate || touched) && !isValid,
    },
    className,
  )

  const inputFieldClassName = cn(
    'form-control',
    sizeClassNames[size],
    { error: !(shouldValidate || touched) && !isValid },
    inputClassName,
  )

  // Determine input type for password visibility toggle
  const inputType = type === 'password' && showPassword ? 'text' : type

  // Shared props for input/textarea
  const sharedProps = {
    className: inputFieldClassName,
    disabled,
    id,
    name,
    onBlur: blurHandler,
    onFocus: focusHandler,
    onKeyDown: keyDownHandler,
    placeholder,
    value,
    ...rest,
  }

  return (
    <div className={containerClassName}>
      {textarea ? (
        <textarea
          {...sharedProps}
          ref={ref as ForwardedRef<HTMLTextAreaElement>}
        />
      ) : (
        <>
          {iconLeft && (
            <span
              onClick={onIconLeftClick}
              className={cn('input-icon icon-left', {
                'icon-disabled': disabled,
                'pe-none': !onIconLeftClick,
              })}
            >
              {iconLeft}
            </span>
          )}
          <input
            {...sharedProps}
            type={inputType}
            ref={ref as ForwardedRef<HTMLInputElement>}
          />
          {iconRight ? (
            <span
              onClick={onIconRightClick}
              className={cn('input-icon icon-right', {
                'icon-disabled': disabled,
                'pe-none': !onIconRightClick,
              })}
            >
              {iconRight}
            </span>
          ) : (
            type === 'password' && (
              <i
                onClick={togglePasswordVisibility}
                className={cn('input-icon icon-right', {
                  'fas fa-eye': !showPassword,
                  'fas fa-eye-slash': showPassword,
                  'icon-disabled': disabled,
                })}
              />
            )
          )}
        </>
      )}
      {children}
    </div>
  )
}

Input.displayName = 'Input'
export default Input
